import { Checkbox, Grid, Radio, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import { notificationSettingsKeys } from "../../../../../../api/queryKeys";
import { operationStatus } from "../../../../../../components/ActionStatus";
import { field_IDs_mapping } from "../../../fieldMapping";
import DropDownButton from "./DropDownButton";

import { frequencyType } from "./FrequencyNotificationsSection";

export const ALL_OPTIONS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export default function DaySelector({
  method,
  setFetchStatus,
  defaultSelectedDays,
  selectedFrequencyType,
}) {
  const [selectedDays, setSelectedDays] = useState(defaultSelectedDays);
  const { updateNotificationSetting } = useAPI();
  const colors = useColors();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      selectedFrequencyType === frequencyType.WEEKLY &&
      selectedDays.length !== 1
    )
      setSelectedDays([ALL_OPTIONS[0]]);
  }, []);

  useEffect(() => {
    setSelectedDays(defaultSelectedDays);
  }, [defaultSelectedDays]);

  const getButtonText = () => {
    if (selectedFrequencyType === frequencyType.DAILY) {
      return ALL_OPTIONS.filter((day) => selectedDays.includes(day))
        .map((day) => day.substring(0, 3))
        .join(", ");
    } else {
      return selectedDays[0];
    }
  };

  const noChanges = () => {
    if (defaultSelectedDays.length !== selectedDays.length) return false;
    const defaultSorted = [...defaultSelectedDays].sort();
    const selectedSorted = [...selectedDays].sort();
    return defaultSorted.every(
      (value, index) => value === selectedSorted[index]
    );
  };

  const buttonText = getButtonText();

  const handleOnMenuClose = () => {
    if (noChanges()) return;
    setFetchStatus(operationStatus.LOADING);
    updateNotificationSetting(
      method,
      field_IDs_mapping.FREQUENCY.DAYS,
      selectedDays
    )
      .then(() => {
        setFetchStatus(operationStatus.SUCCESS);
        queryClient.invalidateQueries({
          queryKey: notificationSettingsKeys.all,
        });
      })
      .catch(() => {
        setFetchStatus(operationStatus.ERROR);
        setSelectedDays(defaultSelectedDays);
      });
  };

  const handleCheckboxClick = (day) => {
    selectedDays.includes(day)
      ? setSelectedDays((previousSelectedDays) =>
          previousSelectedDays.filter((selectedDay) => selectedDay !== day)
        )
      : setSelectedDays((previousSelectedDays) => [
          ...previousSelectedDays,
          day,
        ]);
  };

  return (
    <DropDownButton
      text={buttonText}
      onClose={handleOnMenuClose}
      sx={{ width: "227px" }}
    >
      <Grid container direction="column" width="225px" key={method}>
        {ALL_OPTIONS.map((day) => (
          <Grid item key={day}>
            {selectedFrequencyType === frequencyType.DAILY ? (
              <>
                <Checkbox
                  checked={selectedDays.includes(day)}
                  onClick={() => handleCheckboxClick(day)}
                  sx={{
                    transform: "scale(0.8)",
                    "&.Mui-checked": {
                      color: colors.primary_main,
                    },
                    "& .MuiSvgIcon-root": {
                      fill: colors.primary_main,
                    },
                  }}
                />
                <Typography variant="caption">{day}</Typography>
              </>
            ) : (
              <>
                <Radio
                  value={day}
                  checked={selectedDays.includes(day)}
                  onClick={() =>
                    !selectedDays.includes(day) && setSelectedDays([day])
                  }
                  sx={{
                    transform: "scale(0.8)",
                    "&.Mui-checked": {
                      color: colors.primary_main,
                    },
                    "& .MuiSvgIcon-root": {
                      fill: colors.primary_main,
                    },
                  }}
                />
                <Typography variant="caption">{day}</Typography>
              </>
            )}
          </Grid>
        ))}
      </Grid>
    </DropDownButton>
  );
}
